import React, { FC, useMemo } from 'react';
import _ from 'lodash';
import Slider from 'react-slick';
import MagicSliderDots from 'react-magic-slider-dots';
import { Box, Image, Proportion } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials-standalone';
import { ChevronLeft, ChevronRight } from '@wix/wix-ui-icons-common';
import { MediaSource } from '@wix/ambassador-partners-marketplace-v1-template-listing/types';
import CustomArrowWrapper from '@components/Template/Carousels/CustomArrowWrapper';
import { fillImageUrl } from '@utils/urlUtils';
import CarouselStudioBranding from '@components/Templates/TemplateGallery/CarouselStudioBranding';
import 'react-magic-slider-dots/dist/magic-dots.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import s from '../TemplateCard.module.scss';

interface Props {
  imageSources?: MediaSource[];
  focused: boolean;
  hover?: boolean;
  animatedPreview?: MediaSource;
  setFocused?: (_focused: boolean) => void;
}

const CardCarousel: FC<Props> = ({
  imageSources,
  animatedPreview,
  setFocused,
}) => {
  const imagesCount = _.size(imageSources);
  const isMoreThanSingleImage = imagesCount > 1;

  const { t } = useTranslation();
  const mobileViewSettings = useMemo(
    () => ({
      cssEase: 'linear',
      autoplay: false,
      autoplaySpeed: 2000,
      lazyLoad: 'progressive',
      slidesToShow: 1,
      slidesToScroll: 1,
      infinite: isMoreThanSingleImage,
      draggable: isMoreThanSingleImage,
      swipe: isMoreThanSingleImage,
      swipeToSlide: isMoreThanSingleImage,
      dots: isMoreThanSingleImage,
      ...(isMoreThanSingleImage
        ? {
            className: s.cardCarouselMobile,
            appendDots: (dots: any) => (
              <div
                data-hook="dots-wrapper"
                onClick={(e) => {
                  e.stopPropagation();
                }}
                style={{
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'center',
                  bottom: '17px',
                  zIndex: 2,
                }}
                tabIndex={-1}
                aria-hidden="true"
                role="presentation"
              >
                <MagicSliderDots
                  dots={dots}
                  numDotsToShow={5}
                  dotWidth={10}
                  dotContainerClassName="magic-dots slick-dots magic-dots-container"
                />
              </div>
            ),
            nextArrow: (
              <CustomArrowWrapper hover={true}>
                <ChevronRight
                  onClick={() => setFocused?.(true)}
                  color="#ffffff"
                  aria-label={t(
                    'marketplace.templates-page.right-arrow-aria-label',
                  )}
                />
              </CustomArrowWrapper>
            ),
            prevArrow: (
              <CustomArrowWrapper hover={true}>
                <ChevronLeft
                  onClick={() => setFocused?.(true)}
                  color="#ffffff"
                  aria-label={t(
                    'marketplace.templates-page.left-arrow-aria-label',
                  )}
                />
              </CustomArrowWrapper>
            ),
          }
        : {
            nextArrow: <span tabIndex={-1}></span>,
            prevArrow: <span tabIndex={-1}></span>,
          }),
    }),
    [imagesCount],
  );

  return (
    <div
      style={{
        overflow: 'hidden',
        position: 'relative',
        height: '100%',
      }}
    >
      <Box direction="vertical" gap="18px" className={s.cardCarousel}>
        <Slider {...mobileViewSettings}>
          {_.map(imageSources, (slide, id) => (
            <CarouselImage
              url={slide.url}
              animatedPreview={animatedPreview}
              key={id}
              description={slide.description}
            />
          ))}
          {imagesCount === 0 && <CarouselImage url={''} description={''} />}
        </Slider>
      </Box>
      {<CarouselStudioBranding isMobileView={true} />}
    </div>
  );
};

const CarouselImage: FC<
  MediaSource & {
    hover?: boolean;
    focused?: boolean;
    isMobileView?: boolean;
    animatedPreview?: MediaSource;
  }
> = ({ url = '', description }) => {
  return (
    <Box position="relative" overflow="hidden" padding="20px 20px 0 20px">
      <Proportion key={url} aspectRatio={8 / 5}>
        <Image
          height="100%"
          className={s.image}
          dataHook={`slide-${url}`}
          fit="cover"
          borderRadius="2px 2px 0 0"
          transparent
          alt={description || 'template image'}
          src={fillImageUrl(url, 972, 607)}
        />
      </Proportion>
    </Box>
  );
};

export default CardCarousel;
